import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  styled,
  TextField,
  Tooltip
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { Autocomplete } from '@material-ui/lab';
import { Field, FieldProps, useFormikContext } from 'formik';
import ContentStrand, { ContentStrandMapping } from 'models/ContentStrand';
import ContentSubject, { ContentSubjectDescriptions } from 'models/ContentSubject';
import React from 'react';
import { LessonForm, SubjectArea } from '../../types';

const PaddedPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

const ChildGrid = styled(Grid)(({ theme }) => ({
  // Extra specificity required to override MUI styles
  '&&': {
    paddingLeft: theme.spacing(6),
  },
}));

interface Props {
  index: number;
  disableAddButton?: boolean;
  disableRemoveButton?: boolean;
  onAddClick: () => void;
  onRemoveClick: () => void;
  availableSubjects: ContentSubject[];
}

const SubjectSection: React.FC<Props> = ({
  index,
  disableAddButton = false,
  disableRemoveButton = false,
  onAddClick,
  onRemoveClick,
  availableSubjects,
}) => {
  const { values, setFieldValue } = useFormikContext<LessonForm>();

  const handleContentChangeChange = (
    _: React.ChangeEvent<{}>,
    value: ContentStrand | ContentStrand[] | null,
  ) => {
    setFieldValue(`subjectAreas[${index}].contentStrands`, value);
  };

  return (
    <PaddedPaper elevation={4}>
      <Grid direction="column" spacing={6} container>
        <Grid item>
          <Field name={`subjectAreas[${index}].subject`}>
            {({ field, form, meta }: FieldProps<SubjectArea['subject']>) => {
              function onChange(
                e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
              ) {
                form.setFieldValue(`subjectAreas[${index}].contentStrands`, []);
                form.setFieldValue(`subjectAreas[${index}].mnStandards`, '');
                field.onChange(e);
              }

              return (
                <FormControl error={meta.touched && Boolean(meta.error)} required fullWidth>
                  <InputLabel id={`subject-name-label-${index}`} shrink>
                    Subject Name
                  </InputLabel>
                  <Select
                    id={`subject-name=${index}`}
                    labelId={`subject-name-label-${index}`}
                    {...field}
                    onChange={onChange}
                  >
                    {Object.values(ContentSubject).map((subject, index) => (
                      <MenuItem
                        key={index}
                        value={subject}
                        disabled={!availableSubjects.includes(subject)}
                      >
                        {ContentSubjectDescriptions[subject]}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{meta.touched && meta.error}</FormHelperText>
                </FormControl>
              );
            }}
          </Field>
        </Grid>
        {values.subjectAreas[index].subject !== ContentSubject.ComputerScience &&
        <>
        <ChildGrid item>
          <Field name={`subjectAreas[${index}].contentStrands`}>
            {({ field, meta, form }: FieldProps<SubjectArea['contentStrands']>) => {
              const subject = values.subjectAreas[index].subject as ContentSubject | '';

              return (
                <Tooltip title={subject === '' ? 'Please select a subject' : ''}>
                  <Autocomplete<ContentStrand, boolean>
                    {...field}
                    multiple
                    id={`content-strands-${index}`}
                    options={subject && subject !== ContentSubject.ComputerScience ? Object.values(ContentStrandMapping[subject]) : []}
                    disableCloseOnSelect
                    disabled={subject === ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Content Strands"
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                        }}
                        helperText={meta.touched && meta.error}
                        error={meta.touched && Boolean(meta.error)}
                      />
                    )}
                    onChange={handleContentChangeChange}
                    onBlur={(e) => {
                      form.setFieldTouched(`subjectAreas[${index}].contentStrands`, true);
                      field.onBlur(e);
                    }}
                  />
                </Tooltip>
              );
            }}
          </Field>
        </ChildGrid>
        <ChildGrid item>
          <Field name={`subjectAreas[${index}].mnStandards`}>
            {({ field, meta }: FieldProps<SubjectArea['mnStandards']>) => (
              <TextField
                id={`mn-standards-${index}`}
                label="MN Standards"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={
                  meta.touched && Boolean(meta.error)
                    ? meta.error
                    : 'Please add the code for each relevant standard, using the format X.X.X.X. Separate standards using a comma. (e.g. - 1.2.1.1, 0.3.4.8, 0.1.1.1.2)'
                }
                error={meta.touched && Boolean(meta.error)}
                {...field}
              />
            )}
          </Field>
          </ChildGrid>
          </>
        }
        <Grid spacing={2} container item>
          {!disableAddButton && (
            <Grid item>
              <Button
                color="primary"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAddClick}
              >
                Add Section
              </Button>
            </Grid>
          )}
          {!disableRemoveButton && (
            <Grid item>
              <Button variant="outlined" startIcon={<RemoveIcon />} onClick={onRemoveClick}>
                Remove Section
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </PaddedPaper>
  );
};

export default SubjectSection;
